import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { TagIcon } from "@heroicons/react/outline";
import * as Sentry from "@sentry/browser";
import axios from "axios";
import ContactTagForm, {
  ContactTagFormSubmit,
} from "src/containers/tags/ContactTagForm";
import HeaderNavigation from "src/components/HeaderNavigation";
import * as meta from "src/containers/tags/ContactTagMeta";
import useCleanTimeout from "src/hooks/useCleanTimeout";
import { API_ROUTES, buildAPIRoute, buildRoute } from "src/router/router";
import { axiosErr } from "src/types/axios";

export default function ContactTagCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const timeout = useCleanTimeout();


  const formSubmit: ContactTagFormSubmit = async (
    formData,
    setSubmitting,
    setBackendValidationErrors,
    setBackendError
  ) => {
    setBackendValidationErrors({});
    setBackendError("");
    try {
      await axios.post(
        buildAPIRoute(API_ROUTES.CONTACT_TAG_CREATE_LIST),
        formData
      );
      setSubmitting(false);
      setSuccess(true);
      timeout.current = setTimeout(() => {
        navigate(buildRoute("config-inbox-contact-tag-list"));
      }, 1000 * 2);
    } catch (err) {
      const errorAxios: axiosErr = err as axiosErr;
      setSuccess(false);
      if (errorAxios?.response?.status === 400) {
        Sentry.captureException(err);
        setBackendValidationErrors(errorAxios.response.data);
      } else {
        setBackendError(
          t(
            "settings:contact-tags.contact-tag-create.error",
            "An error occurred while creating your contact tag. Please try again."
          )
        );
      }
      timeout.current = setTimeout(() => {
        setSubmitted(false);
      }, 1000 * 2);
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="h-screen overflow-y-auto bg-gray-50">
      <div className="py-4 px-4 sm:border-b sm:border-gray-200 sm:px-6 lg:px-8">
        <HeaderNavigation
          navPages={[
            {
              name: t("home"),
              url: buildRoute("dashboard"),
            },
            {
              name: t("settings:account-config.title"),
              url: buildRoute("config-account"),
            },
            {
              name: t("contact-tags", "Contact tags"),
              url: buildRoute("config-inbox-contact-tag-list"),
            },
            {
              name: t(
                "settings:contact-tags.contact-tag-create.title",
                "Create Contact Tag"
              ),
              url: buildRoute("config-inbox-contact-tag-create"),
            },
          ]}
        />

        <div className="lg:flex lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="mt-2 flex items-center text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl">
              <TagIcon className="mr-2 h-7 w-7" aria-hidden="true" />
              {t(
                "settings:contact-tags.contact-tag-create.title",
                "Create Contact Tag"
              )}
            </h2>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mt-6 max-w-3xl">
         
            <ContactTagForm
              contactTag={meta.getInitialValues()}
              formSubmit={formSubmit}
              success={success}
              submitted={submitted}
              action="create"
            />
          
        </div>
      </div>
    </div>
  );
}
